import {
  AllTournamentsPagination,
  ExistingGame,
  TournamentTagsEnum,
} from '@fantaz/shared/data-access/models';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  GAMEDETAILS_FEATURE_KEY,
  GameDetailsState,
} from './game-details.reducer';
import { shuffle } from '@fantaz/shared/utils/filters';

export const getGameDetailsState = createFeatureSelector<GameDetailsState>(
  GAMEDETAILS_FEATURE_KEY,
);

export const getSearchGameDetailsTerm = createSelector(
  getGameDetailsState,
  (state: GameDetailsState): string => state.searchTerm || '',
);

export const getFilterByTags = createSelector(
  getGameDetailsState,
  (state: GameDetailsState): string[] => state.filterByTags || [],
);

export const getGameDetailsLoaded = createSelector(
  getGameDetailsState,
  (state: GameDetailsState) => {
    return state.loaded;
  },
);

export const getGameDetailsFilteringState = createSelector(
  getGameDetailsState,
  (state: GameDetailsState) => state.filtering,
);

export const loadingMoreGameDetails = createSelector(
  getGameDetailsState,
  (state: GameDetailsState) => state.loadingMore,
);

export const getGameDetailsSelectedTags = createSelector(
  getGameDetailsState,
  (state: GameDetailsState) => state.filterByTags || [],
);

export const getGameDetailsError = createSelector(
  getGameDetailsState,
  (state: GameDetailsState) => state.error,
);

export const getExistingGameIds = createSelector(
  getGameDetailsState,
  (state: GameDetailsState): ExistingGame => {
    const cashTournaments = state.cashTournaments?.items || [];
    const prizeTournaments = state.prizeTournaments?.items || [];
    return {
      total: cashTournaments.length + prizeTournaments.length,
      existingGameIds: state.existingGameIds,
    };
  },
);

export const getAllGameDetails = createSelector(
  getGameDetailsState,
  ({ cashTournaments, prizeTournaments }) => {
    let tournaments = cashTournaments?.items || [];

    if (prizeTournaments?.items?.length) {
      tournaments = [...tournaments, ...prizeTournaments.items];
    }
    return tournaments;
  },
);

export const getTournamentsResponse = createSelector(
  getGameDetailsState,
  (state: GameDetailsState) => state,
);

export const getTournamentsPagination = createSelector(
  getGameDetailsState,
  ({
    cashTournaments,
    prizeTournaments,
    pagesLoaded,
  }): AllTournamentsPagination => {
    return {
      cashTournaments: {
        total: cashTournaments?.total || 0,
        pageIndex: cashTournaments?.pageIndex || 0,
        pageSize: cashTournaments?.pageSize || 10,
        itemsLen: cashTournaments?.items.length || 0,
        pagesLoaded: pagesLoaded.cash || [],
      },
      prizeTournaments: {
        total: prizeTournaments?.total || 0,
        pageIndex: prizeTournaments?.pageIndex || 0,
        pageSize: prizeTournaments?.pageSize || 10,
        comingSoonTotal: prizeTournaments?.comingSoonTotal,
        leptTotal: prizeTournaments?.leptTotal,
        itemsLen: prizeTournaments?.items.length || 0,
        pagesLoaded: pagesLoaded.prize || [],
      },
      total: (prizeTournaments?.total || 0) + (cashTournaments?.total || 0),
    };
  },
);

export const getSelectedGameDetailsId = createSelector(
  getGameDetailsState,
  (state: GameDetailsState) => state.selectedId,
);

export const getSelectedGameDetails = createSelector(
  getGameDetailsState,
  (state: GameDetailsState) => state.selectedId,
);

export const getGame = createSelector(
  getGameDetailsState,
  (state: GameDetailsState) => state.game,
);

export const getEventsCountByTag = createSelector(
  getGameDetailsState,
  (state: GameDetailsState) => state.eventsCountByTag || [],
);

export const getEsRangeValues = createSelector(
  getGameDetailsState,
  (state: GameDetailsState) => state.esRangeValues,
);

export const getTournamentTags = createSelector(
  getTournamentsResponse,
  ({ eventsCountByTag }) => {
    const tagsOrder = Object.values(TournamentTagsEnum);
    return eventsCountByTag
      ?.map(
        ({ tag }) =>
          TournamentTagsEnum[
            tag
              .replace(/\s/g, '_')
              .trim()
              .toUpperCase() as keyof typeof TournamentTagsEnum
          ],
      )
      .filter((tag) => tag !== undefined)
      .sort(
        (a, b) => tagsOrder.indexOf(a) - tagsOrder.indexOf(b),
      ) as TournamentTagsEnum[];
  },
);
